import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { from } from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { VendorAndProductDialogComponent } from '../../../dialogs/vendor-and-product-dialog.component';
import { Vendor } from 'it/primatoo/openapi/api/sec/schemas/cybersecurity/Vendor';
import { Product } from 'it/primatoo/openapi/api/sec/schemas/cybersecurity/Product';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';

@Component({
  standalone: true,
  selector: 'pt-details-security-tool-type-vendor-or-product',
  template: `
    <div class="vendor-or-product">
      <div class="mb-3">
        <label>{{ 'LABELS.VENDOR' | translate }}</label>
        <div class="input-group input-group-sm input-group-clickable">
          <input
            type="text"
            class="form-control cursor-pointer"
            readonly
            placeholder=""
            (click)="browseVendor()"
            [value]="currentVendor ? currentVendor.name : ''"
          />
          <button
            class="btn btn-outline-secondary btn-sm"
            style="border-radius: 0"
            type="button"
            (click)="browseVendor()"
          >
            {{ 'BUTTON.BROWSE' | translate }}
          </button>
        </div>
        <small *ngIf="invalidVendorUUID" class="form-text text-muted">{{
          'ERROR_MESSAGE.INVALID_VENDOR_UUID' | translate
        }}</small>
      </div>

      <div class="mb-3">
        <label>{{ 'LABELS.PRODUCT' | translate }}</label>
        <div class="input-group input-group-sm input-group-clickable">
          <input
            type="text"
            class="form-control cursor-pointer"
            readonly
            placeholder=""
            [disabled]="!currentVendor"
            (click)="browseProduct()"
            [value]="currentProduct ? currentProduct.name : ''"
          />
          <button
            class="btn btn-outline-secondary btn-sm"
            style="border-radius: 0"
            type="button"
            [disabled]="!currentVendor"
            (click)="browseProduct()"
          >
            {{ 'BUTTON.BROWSE' | translate }}
          </button>
        </div>
      </div>

      <div class="mb-3">
        <label>{{ 'LABELS.VERSION' | translate }}</label>
        <div class="input-group input-group-sm">
          <input
            type="text"
            class="form-control"
            placeholder=""
            [(ngModel)]="currentVersion"
            (ngModelChange)="changeVersion($event)"
          />
        </div>
      </div>
    </div>
  `,
  imports: [TranslateModule, FormsModule, NgIf],
  styles: [
    `
      .vendor-or-product {
        gap: 5px;
        display: flex;
        margin: 5px 15px 0 20px;
        justify-content: space-between;
      }

      .input-group-clickable:hover {
        cursor: pointer;
        border-color: #0d6efd;
      }

      .input-group-clickable input:hover {
        color: white;
        border-color: var(--bs-gray);
        background-color: var(--bs-gray);
      }
    `,
  ],
})
export class DetailsSecurityToolTypeVendorOrProductComponent
  implements OnDestroy, OnChanges
{
  @Input() vendor?: Vendor;
  @Input() product?: Product;
  @Input() version?: string;
  @Output() updateVendorProduct =
    new EventEmitter<VendorProductDialogResponse>();

  public currentVendor?: Vendor;
  public currentProduct?: Product;
  public currentVersion?: string;
  public invalidVendorUUID = false;
  private modalRef?: NgbModalRef;

  constructor(private modalService: NgbModal) {}

  ngOnChanges(): void {
    this.invalidVendorUUID = false;
    if (this.vendor && this.currentVendor?.uuid !== this.vendor.uuid) {
      this.currentVendor = this.vendor;
    }

    if (this.version) {
      this.currentVersion = this.version;
    }

    if (
      this.product &&
      this.currentProduct?.uuid !== this.product.uuid &&
      this.vendor.uuid
    ) {
      this.currentProduct = this.product;
    }
  }

  browseVendor() {
    this.modalRef = this.modalService.open(VendorAndProductDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: 'md',
    });
    this.modalRef.componentInstance.startFindVendor(this.currentVendor);
    from(this.modalRef.result).subscribe((response) => {
      if (response) {
        this.currentVendor = response;
        this.currentProduct = undefined;
      }

      const output: VendorProductDialogResponse = {
        vendor: this.currentVendor,
        product: this.currentProduct,
        version: this.currentVersion,
      };
      this.updateVendorProduct.emit(output);
    });
  }

  browseProduct() {
    this.modalRef = this.modalService.open(VendorAndProductDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: 'md',
    });
    this.modalRef.componentInstance.startFindProduct(
      this.currentVendor,
      this.currentProduct
    );
    from(this.modalRef.result).subscribe((response) => {
      if (response) {
        this.currentProduct = response;
      }

      const output: VendorProductDialogResponse = {
        vendor: this.currentVendor,
        product: this.currentProduct,
        version: this.currentVersion,
      };
      this.updateVendorProduct.emit(output);
    });
  }

  changeVersion(v: string) {
    this.currentVersion = v;
    const output: VendorProductDialogResponse = {
      vendor: this.currentVendor,
      product: this.currentProduct,
      version: this.currentVersion,
    };
    this.updateVendorProduct.emit(output);
  }

  ngOnDestroy(): void {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }
}

export interface VendorProductDialogResponse {
  vendor?: Vendor;
  product?: Product;
  version?: string;
}
